import { Command } from 'cmdk';
import { lowerCase, startCase } from 'es-toolkit';
import { CheckIcon, XCircle } from 'lucide-react';
import { ReactNode } from 'react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ButtonGroup } from '../ui/button-group';
import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Separator } from '../ui/separator';
import { cn } from '../ui/utils';

export type Option = {
  label: string;
  value: string;
};

export interface DataTableFacetProps {
  disabled?: boolean;
  icon?: ReactNode;
  onChange: (selectedValues: string[]) => void;
  options: Option[] | Record<string, Option[]>;
  selected: string[];
  title: string;
}

export function DataTableFacet({
  disabled,
  icon,
  onChange,
  options,
  selected,
  title,
}: DataTableFacetProps) {
  const selectedValues = new Set(selected);

  return (
    <Popover>
      <ButtonGroup className={cn('**:text-xs')}>
        {selectedValues.size > 0 && (
          <Button size="icon-sm" variant="outline" onClick={() => onChange([])}>
            <XCircle />
            <span className="sr-only">Clear</span>
          </Button>
        )}
        <PopoverTrigger asChild>
          <Button
            aria-label={title}
            className="px-2 py-1 transition-colors"
            disabled={disabled}
            role="combobox"
            size="sm"
            variant="outline">
            {icon}
            {title}
            {selectedValues?.size > 0 && (
              <div className="-mr-1 flex gap-1">
                <Separator orientation="vertical" />
                {selectedValues.size > 2 ? (
                  <Badge className="rounded-sm" variant="secondary">
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  <div className="flex items-center gap-1">
                    {Array.from(selectedValues).map((value) => {
                      return (
                        <Badge key={value} className="rounded-sm" variant="secondary">
                          {value}
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </Button>
        </PopoverTrigger>
      </ButtonGroup>
      <PopoverContent
        align="start"
        alignOffset={selectedValues.size > 0 ? -32 : 0}
        className="w-full p-0 **:text-xs">
        <Command
          filter={(value, search) => {
            const normalizedValue = value + ' ' + lowerCase(value);
            return new RegExp(search, 'gi').test(normalizedValue) ? 1 : 0;
          }}>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            {Array.isArray(options) ? (
              <CommandGroup>
                {options.map((option, index) => {
                  const isSelected = selectedValues.has(option.value);
                  return (
                    <Option
                      key={`${option.value}-${index}`}
                      isSelected={isSelected}
                      option={{ label: startCase(option.label), value: option.value }}
                      selectedValues={selectedValues}
                      onChange={onChange}
                    />
                  );
                })}
              </CommandGroup>
            ) : (
              Object.entries(options).map(([group, groupOptions]) => (
                <CommandGroup key={group} heading={startCase(group)}>
                  {groupOptions.map((option) => {
                    const isSelected = selectedValues.has(option.value);
                    return (
                      <Option
                        key={`${option.value}-${group}`}
                        group={group}
                        isSelected={isSelected}
                        option={option}
                        selectedValues={selectedValues}
                        onChange={onChange}
                      />
                    );
                  })}
                </CommandGroup>
              ))
            )}

            {selectedValues.size > 0 && (
              <div className="sticky bottom-0 rounded-br-sm rounded-bl-sm bg-card">
                <CommandSeparator />
                <CommandGroup>
                  <CommandList>
                    <CommandItem className="justify-center text-xs" onSelect={() => onChange([])}>
                      Clear Filter
                    </CommandItem>
                  </CommandList>
                </CommandGroup>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const Option = ({
  group,
  isSelected,
  onChange,
  option,
  selectedValues,
}: {
  isSelected: boolean;
  option: Option;
  onChange: (value: string[]) => void;
  selectedValues: Set<string>;
  group?: string;
}) => {
  return (
    <CommandItem
      data-testid={`${option.value}-${group}`}
      value={group ? `${option.value}-${group}` : option.value}
      onSelect={() => {
        void (isSelected ? selectedValues.delete(option.value) : selectedValues.add(option.value));
        onChange(Array.from(selectedValues));
      }}>
      <div
        className={cn(
          'flex size-4 place-content-center rounded-sm border border-primary',
          isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
        )}>
        <CheckIcon />
      </div>
      <span>{option.label}</span>
    </CommandItem>
  );
};
